import { Button, HeroControl, HeroControlPanel } from '@wundo/ui';
import { Circle } from 'lucide-react';
import { offlineRecordingClock } from './offlineRecordingClock.ts';
import { m } from '@/paraglide/messages.js';

export interface OfflineRecordingControlProps {
  /** Whether this device is holding the microphone right now. */
  live: boolean;
  /** Seconds so far, for the clock on the pill. */
  elapsed: number;
  /** A piece of audio was lost for good; the rest is still being recorded. */
  chunkLost: boolean;
  /** While a start or a stop is in flight. */
  pending: boolean;
  /** Put the running recording back in front of the page. */
  onOpen: () => void;
  onStop: () => void;
  onResume: () => void;
}

/**
 * The one control that shows itself: a recording in progress.
 *
 * It sits in the same row as the settings, wears the same pill, and is the only
 * one of them that is red — the state it reports is one nobody may discover
 * afterwards. The clock is the proof: a number that does not move is a
 * recording that is not running, and the user can see that at a glance without
 * opening anything.
 *
 * While the recording runs the pill is the minimised form of the modal that
 * holds it, and nothing else: pressing it puts the recording back in front of
 * the page. A popover here would be a second, quieter place to stop a recording
 * — the one act on this page that deserves exactly one.
 *
 * An interrupted recording is a panel again, because there is nothing running
 * to hold the screen with. It still wears the red pill: from the server's side
 * the recording is open, holding everything already captured, so hiding it
 * would leave audio on a meeting with nothing on screen to say so.
 */
export function OfflineRecordingControl({
  live,
  elapsed,
  chunkLost,
  pending,
  onOpen,
  onStop,
  onResume,
}: OfflineRecordingControlProps) {
  /*
   * A filled dot rather than a microphone: this is the recording indicator
   * every device in the room already uses, and it is the one icon on the row
   * that is not naming a setting.
   */
  const dot = <Circle aria-hidden='true' className='fill-danger' />;

  if (live) {
    return (
      <HeroControl icon={dot} onClick={onOpen}>
        {m.meeting_offline_recording_pill({
          time: offlineRecordingClock(elapsed),
        })}
      </HeroControl>
    );
  }

  return (
    <HeroControlPanel
      icon={dot}
      value={m.meeting_offline_interrupted_pill()}
      title={m.meeting_offline_interrupted_title()}
      description={m.meeting_offline_interrupted_body()}
    >
      {chunkLost && (
        <p role='alert' className='mt-2.5 text-sm text-danger'>
          {m.meeting_offline_chunk_lost()}
        </p>
      )}

      <div className='mt-3.5 flex flex-wrap gap-2'>
        <Button size='sm' disabled={pending} onClick={onResume}>
          {m.meeting_offline_resume()}
        </Button>
        <Button size='sm' variant='quiet' disabled={pending} onClick={onStop}>
          {m.meeting_offline_finish()}
        </Button>
      </div>
    </HeroControlPanel>
  );
}
